import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Button
} from '@mui/material';
import {
  getCurrentDateYYYYMMDD
} from "../common/commonMethod";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { FxWindow } from "../../fxms/windows/FxWindows";


const MuiTable = ({
  tableRowDataList,
  rows,
  menuFullName,
  interval,
  onSelectRow,
  buttonColumnId,
  selectedRowIndex,
  groupColumnId,
  className = 'cmTable',
  clickRepeat = false,
  doubleClickToSubmit = false,
  onDoubleClickRow = null,
  printRef = null,
  exelDown = true,
  excelRows,
  initialRowsPerPage = 10
}) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [groupedRows, setGroupedRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);  
  const [clickTimeout, setClickTimeout] = useState(null);

  useEffect(() => {
    setSelectedRow(selectedRowIndex);
  }, [selectedRowIndex]);

  useEffect(() => {
    if (groupColumnId !== null && groupColumnId !== undefined) {
      const groupRows = () => {
        const groupedCounts = rows.reduce((acc, row, index, array) => {
          if (
            index === 0 ||
            row[groupColumnId]?.props?.children !==
              array[index - 1][groupColumnId]?.props?.children
          ) {
            acc.push(1);
          } else {
            acc[acc.length - 1]++;
          }
          return acc;
        }, []);

        let lastValue = null;
        let currentGroupIndex = -1;
        const newRows = [];

        rows.forEach((row, rowIndex) => {
          if (
            row[groupColumnId]?.props?.children !== lastValue?.props?.children
          ) {
            lastValue = row[groupColumnId];
            currentGroupIndex++;
            newRows.push({
              ...row,
              _rowSpan: groupedCounts[currentGroupIndex],
            });
          } else {
            const { year, ...restOfRow } = row;
            newRows.push({ ...restOfRow });
          }
        });

        setGroupedRows(newRows);
      };

      groupRows();
    } else {
      setGroupedRows(rows);
    }
  }, [rows, groupColumnId]);

  const handleRowClick = (index) => {
    if (doubleClickToSubmit) {
      if (clickTimeout === null) {
        setClickTimeout(
          setTimeout(() => {
            setClickTimeout(null);
            if (selectedRow !== index || clickRepeat) {
              setSelectedRow(index);
              onSelectRow && onSelectRow(index);
            } else {
              setSelectedRow(null);
              onSelectRow && onSelectRow(null);
            }
          }, 300)
        );
      } else {
        clearTimeout(clickTimeout);
        setClickTimeout(null);
        onDoubleClickRow && onDoubleClickRow(index);
      }
    } else {
      if (selectedRow !== index || clickRepeat) {
        setSelectedRow(index);
        onSelectRow && onSelectRow(index);
      } else {
        setSelectedRow(null);
        onSelectRow && onSelectRow(null);
      }
    }
  };

  const handleCellClick = (event) => {
    event.stopPropagation();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const columnClassNames = {};
  tableRowDataList.forEach((row) => {
    row.forEach((cell) => {
      if (cell.id && cell.className) {
        columnClassNames[cell.id] = cell.className;
      }
    });
  });

  const exportToExcel = async () => {

    let downExcelRows = {};

    if(excelRows != undefined || excelRows != null)
    {
      downExcelRows = excelRows;
    }else{
      downExcelRows = rows;
    }

    if (downExcelRows.length === 0) {
      FxWindow.alert("조회된 데이터가 없습니다.");
      return;
    };

    const colNames = tableRowDataList[0].map(i => i.cellName);
    const sheetName = menuFullName ? menuFullName.replaceAll("/", "_") : "downloadData";

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);
    worksheet.addRow(colNames);
    downExcelRows.forEach(row => worksheet.addRow(Object.values(row)));

    const today = getCurrentDateYYYYMMDD();
    const fileName = menuFullName ? menuFullName + "_" + today + ".xlsx" : "tableData.xlsx";
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fileName);
  };
  

  return (
    <>
    {exelDown? <div className="FxMuiTableFunc">
        <div className="excelDown">
          <Button onClick={exportToExcel}>
            엑셀 다운로드
          </Button>
        </div>
      </div> : null}    

    <Table className={className} stickyHeader={true} ref={printRef}>
    <TableHead>
      {tableRowDataList.map((subList, index) => (
        <TableRow key={index}>
          {subList.map((element, subIndex) => (
            <TableCell
              key={subIndex}
              align="center"
              rowSpan={element.row}
              colSpan={element.col}
              className={element.className}
              style={element.style}
            >
              {element.cellName}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableHead>
    <TableBody>
      {rows && rows.length > 0 ? (
        ((groupedRows || []).length > 0  // groupedRows가 undefined 또는 null이면 빈 배열을 사용
          ? groupedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          : [])
        .map((row, rowIndex) => (
          <TableRow
            className={selectedRow === rowIndex ? "select" : ""}
            key={rowIndex}
            onClick={() => handleRowClick(rowIndex)}
            sx={{
              cursor: "pointer",
            }}
          >
            {Object.keys(row || {}).map((key, cellIndex) => {  // row가 undefined 또는 null인 경우 빈 객체를 사용
              if (key === "_rowSpan") return null;  // Skip the internal _rowSpan key
              const cellValue = row[key];
              const cellClassName = columnClassNames[key] || "";
              const isButtonColumn = key === buttonColumnId;

              return (
                <TableCell
                  key={cellIndex}
                  className={cellClassName}
                  rowSpan={key === groupColumnId ? row._rowSpan : null}
                  onClick={isButtonColumn ? handleCellClick : null}
                >
                  {cellValue}
                </TableCell>
              );
            })}
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={tableRowDataList[0].length} style={{ textAlign: 'center' }}>
            데이터가 존재하지 않습니다
          </TableCell>
        </TableRow>
      )}
      </TableBody>
      </Table>
      {rows && rows.length > 0 ?
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={groupedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        : null}
    </>
  );
};

export default MuiTable;
